import styled from 'styled-components';
import { Icon } from '../../../../../components/common/icon/Icon';
import { IProduct } from '../../../../../interfaces/Product.interfaces';
import { formatPrice } from '../../../../../utils/formatter/formatPrice.util';

interface PriceReceiptInListProps {
  product: IProduct;
}

export const PriceReceiptInList = ({ product }: PriceReceiptInListProps) => {
  return (
    <Container>
      <Header>
        <Icon name="MoneyWon" width={20} height={20} />
        <Title>{'가격 내역'}</Title>
      </Header>

      <Card>
        <PriceSection>
          {product.priceDetails
            .filter((priceInfo) => priceInfo.type === 'ADD')
            .sort((a, b) => b.amount - a.amount)
            .map((priceInfo) => (
              <SpaceBetweenDiv>
                <ThinText>{priceInfo.title}</ThinText>
                <ThinText>{`${priceInfo.amount.toLocaleString()}원`}</ThinText>
              </SpaceBetweenDiv>
            ))}

          {product.priceDetails
            .filter((priceInfo) => priceInfo.type === 'SUBTRACT')
            .sort((a, b) => b.amount - a.amount)
            .map((priceInfo) => (
              <SpaceBetweenDiv>
                <RedChip>
                  <RedChipText>{priceInfo.title}</RedChipText>
                </RedChip>
                <RedText>{`- ${priceInfo.amount.toLocaleString()}원`}</RedText>
              </SpaceBetweenDiv>
            ))}
        </PriceSection>

        <TotalPriceWrapper>
          <SpaceBetweenDiv>
            <BoldText>{'최종가'}</BoldText>
            <BoldText>{`${product.price.toLocaleString()}원`}</BoldText>
          </SpaceBetweenDiv>
        </TotalPriceWrapper>
      </Card>

      <BottomSection>
        <Card>
          <Installment12Wrapper>
            <Icon name={'Calendar12'} width={20} height={20} />
            <SpaceBetweenDiv>
              <BoldText>{'12개월 할부시'}</BoldText>
              <BoldText>{`월 ${formatPrice(
                (product.installmentOf12MonthPrice - product.price * 0.3) / 12,
              )}원`}</BoldText>
            </SpaceBetweenDiv>
          </Installment12Wrapper>

          <Installment24Wrapper>
            <Icon name={'Calendar24'} width={20} height={20} />
            <SpaceBetweenDiv>
              <BoldText>{'24개월 할부시'}</BoldText>
              <BoldText>{`월 ${formatPrice(
                (product.installmentOf24MonthPrice - product.price * 0.3) / 24,
              )}원`}</BoldText>
            </SpaceBetweenDiv>
          </Installment24Wrapper>
        </Card>

        <TipSection>
          <Icon name="ExclamationMarkCircle" width={16} height={16} />
          <TipText>{'30% 선금 기준'}</TipText>
        </TipSection>
      </BottomSection>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;

  margin-top: 16px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: var(--radius-small, 4px);
`;

const Title = styled.h3`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-white, #fff);

  overflow: hidden;
`;

const PriceSection = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-medium, 8px);
  align-self: stretch;

  border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey20, #e4e4ed);
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const TotalPriceWrapper = styled.div`
  display: flex;
  padding: var(--radius-large, 16px) 24px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const Installment12Wrapper = styled.div`
  display: flex;
  padding: var(--radius-large, 16px) 24px;
  align-items: center;
  gap: var(--radius-small, 4px);
  align-self: stretch;

  border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey20, #e4e4ed);
`;

const Installment24Wrapper = styled.div`
  display: flex;
  padding: var(--radius-large, 16px) 24px;
  align-items: center;
  gap: var(--radius-small, 4px);
  align-self: stretch;
`;

const ThinText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const RedText = styled(ThinText)`
  color: #ff3049;
`;

const BoldText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 20.8px */
`;

const SpaceBetweenDiv = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RedChip = styled.div`
  display: flex;
  padding: 6px 8px;
  align-items: center;
  gap: 2px;

  border-radius: var(--radius-small, 4px);
  background: var(--Light-Scheme-Color-secondary-red, #ffdbe0);
`;

const RedChipText = styled.span`
  color: var(--Light-Scheme-Color-primary-red, #ff3049);
  text-align: center;

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-medium, 8px);
  align-self: stretch;
`;

const TipSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2px;
  align-self: stretch;
`;

const TipText = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  text-align: center;

  /* Display/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 15.6px */
`;
